import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Combobox, Field, RadioGroup, type ComboOption } from '../../components/controls';
import { Icon, type IconName } from '../../components/Icon';
import type { ChecklistGate } from '../../domain/settings';
import { api, type AppSettings } from '../../lib/api';
import { FundingLogos } from '../Shell';
import { ALL_CHECKS, parseSalary, type Flow } from '../state';

export type ScreenProps = {
  flow: Flow;
  settings: AppSettings;
  wide: boolean;
  update: (patch: Partial<Flow>) => void;
  go: (screen: Flow['screen']) => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
};

const FEATURES: [IconName, string, string][] = [
  ['user-check', 'Skills-assessed shortlists', 'Candidates assessed on the skills your role needs, before you meet them.'],
  [
    'calendar',
    'See what they can do before you hire',
    "For the first 4 weeks, you cover your candidate's pay at the legal minimum wage while they work with you.",
  ],
  ['shield-check', 'Contracts, payroll, and compliance handled', 'We take care of the paperwork, so you can focus on the work.'],
];

export function Landing({ wide, go, headingRef }: ScreenProps) {
  return (
    <section className="sec">
      <h2 className="disp" style={{ fontSize: 22, lineHeight: 1.25 }} ref={headingRef} tabIndex={-1}>
        What you get
      </h2>
      <ul style={{ marginTop: 16, display: 'grid' }}>
        {FEATURES.map(([icon, title, body]) => (
          <li key={title} className="feat">
            <span className="fi">
              <Icon name={icon} size={18} />
            </span>
            <div>
              <div className="s15" style={{ fontWeight: 600 }}>
                {title}
              </div>
              <div className="s14 mute" style={{ lineHeight: 1.45, marginTop: 2 }}>
                {body}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="cta">
        <Button onClick={() => go('role')}>Check your eligibility</Button>
        <p className="s13 mute" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 12 }}>
          <Icon name="clock" size={14} /> Takes under a minute.
        </p>
        {wide ? null : (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,.08)' }}>
            <FundingLogos />
          </div>
        )}
      </div>
    </section>
  );
}

const UNLISTED = '__unlisted__';

/** Companies House autocomplete. The company number is filled silently; a company not listed can still continue. */
export function CompanyField({ flow, update, error }: { flow: Flow; update: ScreenProps['update']; error?: string | undefined }) {
  const [options, setOptions] = useState<ComboOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const q = flow.companyQuery.trim();
    if (flow.company || flow.companyUnlisted || q.length < 2) {
      setOptions([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await api.companies(q, controller.signal);
        if (result.status === 'unavailable') {
          setUnavailable(true);
          setOptions([{ value: UNLISTED, label: "Continue with the name I've typed" }]);
        } else {
          setUnavailable(false);
          setOptions([
            ...result.items.map((item) => ({
              value: item.companyNumber,
              label: item.name,
              detail: [item.companyNumber, item.address].filter(Boolean).join(' · '),
            })),
            { value: UNLISTED, label: "My company isn't listed" },
          ]);
        }
      } catch {
        if (!controller.signal.aborted) setOptions([{ value: UNLISTED, label: "Continue with the name I've typed" }]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [flow.companyQuery, flow.company, flow.companyUnlisted]);

  return (
    <Field
      label="Company name"
      hint={
        flow.company
          ? `Companies House number ${flow.company.number}`
          : flow.companyUnlisted
            ? "We'll check your company details on a short call."
            : unavailable
              ? "Company search isn't available right now. Type your company name."
              : undefined
      }
      error={error}
    >
      {({ inputId, describedBy, invalid }) => (
        <Combobox
          inputId={inputId}
          value={flow.company?.name ?? flow.companyQuery}
          onInput={(text) => update({ companyQuery: text, company: null, companyUnlisted: false })}
          onPick={(option) =>
            option.value === UNLISTED
              ? update({ companyUnlisted: true, company: null })
              : update({ company: { number: option.value, name: option.label }, companyQuery: option.label, companyUnlisted: false })
          }
          options={options}
          describedBy={describedBy}
          invalid={invalid}
          loading={loading}
          autoComplete="organization"
          placeholder="As registered at Companies House"
        />
      )}
    </Field>
  );
}

export function Role({ flow, settings, update, go, headingRef }: ScreenProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const lookupSeq = useRef(0);
  const role = settings.roles.find((r) => r.title.toLowerCase() === flow.jobTitle.trim().toLowerCase());
  const titleQuery = flow.jobTitle.trim().toLowerCase();
  const titleOptions: ComboOption[] = [
    ...settings.roles.filter((r) => !titleQuery || r.title.toLowerCase().includes(titleQuery)).map((r) => ({ value: r.title, label: r.title })),
    { value: 'Other', label: 'Other' },
  ];

  async function lookupPostcode(): Promise<Flow['postcodeLookup']> {
    const postcode = flow.postcode.trim();
    if (!postcode) return null;
    if (flow.postcodeLookup?.for === postcode) return flow.postcodeLookup;
    const seq = ++lookupSeq.current;
    setCheckingPostcode(true);
    try {
      const result = { ...(await api.postcode(postcode)), for: postcode };
      if (seq === lookupSeq.current) update({ postcodeLookup: result });
      return result;
    } catch {
      const result = { status: 'unavailable' as const, for: postcode };
      if (seq === lookupSeq.current) update({ postcodeLookup: result });
      return result;
    } finally {
      if (seq === lookupSeq.current) setCheckingPostcode(false);
    }
  }

  async function next() {
    const lookup = await lookupPostcode();
    const e: Record<string, string> = {};
    if (!flow.jobTitle.trim()) e.title = 'Choose a job title, or Other.';
    if (!lookup || lookup.status === 'invalid') e.postcode = 'Enter a full UK postcode, for example EC1V 9HX.';
    else if (lookup.status === 'not_found') e.postcode = "We couldn't find that postcode. Check it and try again.";
    if (!(parseSalary(flow.salary) > 0)) e.salary = 'Enter a planned salary.';
    setErrors(e);
    if (Object.keys(e).length === 0) go('you');
  }

  const lookup = flow.postcodeLookup?.for === flow.postcode.trim() ? flow.postcodeLookup : null;

  return (
    <section className="sec">
      <h1 className="disp h2" ref={headingRef} tabIndex={-1}>
        Tell us about the role
      </h1>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void next();
        }}
        style={{ display: 'contents' }}
      >
        <div style={{ display: 'grid', gap: 18, marginTop: 20 }}>
          <Field label="Job title" error={errors.title}>
            {({ inputId, describedBy, invalid }) => (
              <Combobox
                inputId={inputId}
                value={flow.jobTitle}
                onInput={(text) => update({ jobTitle: text })}
                onPick={(option) => update({ jobTitle: option.value })}
                options={titleOptions}
                describedBy={describedBy}
                invalid={invalid}
                placeholder="Start typing, or choose Other"
              />
            )}
          </Field>
          {role?.requiresAiNote ? (
            <Field
              label={
                <span style={{ fontSize: 13 }}>
                  In a sentence, how will this role work with AI systems?{' '}
                  <span className="mute" style={{ fontWeight: 400 }}>
                    (Optional)
                  </span>
                </span>
              }
            >
              {({ inputId }) => (
                <input
                  id={inputId}
                  className="inp"
                  value={flow.aiNote}
                  onChange={(event) => update({ aiNote: event.target.value })}
                  placeholder="For example, evaluating our fraud detection model"
                  style={{ minHeight: 44, padding: '10px 12px', background: '#F7F8FA' }}
                />
              )}
            </Field>
          ) : null}
          <Field label="Where will they be based?" error={errors.postcode}>
            {({ inputId, describedBy, invalid }) => (
              <>
                <input
                  id={inputId}
                  className="inp"
                  value={flow.postcode}
                  onChange={(event) => update({ postcode: event.target.value.toUpperCase() })}
                  onBlur={() => void lookupPostcode()}
                  placeholder="Postcode, for example EC1V 9HX"
                  autoComplete="postal-code"
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  style={{ textTransform: 'uppercase' }}
                />
                <div aria-live="polite" className="s13" style={{ minHeight: 18 }}>
                  {checkingPostcode ? (
                    <span className="mute" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Icon name="loader" size={14} className="spin" /> Finding your region
                    </span>
                  ) : lookup?.status === 'resolved' ? (
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>
                      <Icon name="map-pin" size={14} style={{ color: '#629677' }} />
                      {lookup.inFundedRegion ? lookup.regionName : 'Outside London, East of England, and South West'}
                    </span>
                  ) : lookup?.status === 'unavailable' ? (
                    <span className="mute">We couldn't check your region just now. You can carry on and we'll confirm it on a call.</span>
                  ) : null}
                </div>
              </>
            )}
          </Field>
          <Field label="Planned salary" hint="A rough figure is fine." error={errors.salary}>
            {({ inputId, describedBy, invalid }) => (
              <div style={{ position: 'relative' }}>
                <span className="mute" aria-hidden="true" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>
                  £
                </span>
                <input
                  id={inputId}
                  className="inp num"
                  inputMode="numeric"
                  value={flow.salary}
                  onChange={(event) => update({ salary: event.target.value })}
                  placeholder="28,000"
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  style={{ paddingLeft: 28 }}
                />
              </div>
            )}
          </Field>
        </div>
        <div className="cta">
          <Button type="submit">Next</Button>
        </div>
      </form>
    </section>
  );
}

export function You({ flow, settings, update, go, headingRef }: ScreenProps) {
  const [bandError, setBandError] = useState<string | undefined>();
  return (
    <section className="sec">
      <h1 className="disp h2" ref={headingRef} tabIndex={-1}>
        About your company
      </h1>
      <div style={{ marginTop: 20 }}>
        <RadioGroup
          label="How many people do you employ?"
          variant="tile"
          options={settings.employeeBands}
          value={flow.employeeBand}
          onSelect={(id) => {
            update({ employeeBand: id });
            setBandError(undefined);
          }}
          error={bandError}
        />
      </div>
      <div style={{ marginTop: 24 }}>
        <RadioGroup
          label="What would your organisation find more useful?"
          variant="card"
          options={settings.priorities}
          value={flow.priority}
          onSelect={(id) => update({ priority: id })}
          onCommit={(id) => {
            update({ priority: id });
            if (!flow.employeeBand) {
              setBandError('Choose how many people you employ first.');
              return;
            }
            setTimeout(() => go('check'), 160);
          }}
        />
      </div>
      <p className="s13 mute" style={{ marginTop: 16 }}>
        Tap an answer to continue.
      </p>
    </section>
  );
}

export function Check({ flow, settings, update, go, headingRef }: ScreenProps) {
  return (
    <section className="sec">
      <h1 className="disp h2" ref={headingRef} tabIndex={-1}>
        Do all of these apply?
      </h1>
      {!flow.tickMode ? (
        <>
          <ul style={{ marginTop: 18, display: 'grid', gap: 12 }}>
            {settings.checklist.map((item) => (
              <li
                key={item.gate}
                className="s15"
                style={{ display: 'grid', gridTemplateColumns: '20px minmax(0,1fr)', gap: 12, alignItems: 'start', lineHeight: 1.4 }}
              >
                <Icon name="check" size={18} style={{ color: '#629677', marginTop: 1 }} />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
          <div className="cta">
            <Button
              onClick={() => {
                update({ checks: ALL_CHECKS(true), tickMode: false });
                go('checking');
              }}
            >
              Yes, all of these apply
            </Button>
            <button type="button" className="lnk" onClick={() => update({ tickMode: true, checks: ALL_CHECKS(false) })}>
              Not all of them
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="s14 mute" id="tick-hint" style={{ marginTop: 6 }}>
            Tick the ones that apply.
          </p>
          <fieldset style={{ border: 0, padding: 0, margin: '10px 0 0', display: 'grid' }} aria-describedby="tick-hint">
            <legend className="sr-only">Tick the ones that apply</legend>
            {settings.checklist.map((item) => (
              <Checkbox
                key={item.gate}
                checked={flow.checks[item.gate as ChecklistGate]}
                onChange={(checked) => update({ checks: { ...flow.checks, [item.gate]: checked } })}
              >
                {item.label}
              </Checkbox>
            ))}
          </fieldset>
          <div className="cta">
            <Button onClick={() => go('checking')}>See my result</Button>
          </div>
        </>
      )}
    </section>
  );
}
